import { useEffect, useRef, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Button } from "@/components/PageUI";
import { UpgradeGate } from "@/components/UpgradeGate";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Presentation, Plus, Save, Trash2, Sparkles, MoveRight, MousePointer2, Users, Eraser, X, Loader2, Download } from "lucide-react";
import Pitch from "@/components/tactics/Pitch";
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";

const HOME = "#007AFF";
const AWAY = "#ef4444";
const emptyData = () => ({ tokens: [], arrows: [], texts: [] });

export default function TacticBoard() {
  const { isPremium, sportMeta, club } = useAuth();
  const sport = club?.sport || "soccer";

  const [boards, setBoards] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [name, setName] = useState("Untitled board");
  const [tokens, setTokens] = useState([]);
  const [arrows, setArrows] = useState([]);
  const [mode, setMode] = useState("move"); // move | arrow
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const boardRef = useRef(null);
  const dragId = useRef(null);
  const draft = useRef(null);
  const [draftArrow, setDraftArrow] = useState(null);

  const loadBoards = useCallback(() => {
    if (!isPremium) { setLoading(false); return; }
    api.get("/tactics").then((r) => setBoards(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [isPremium]);
  useEffect(() => { loadBoards(); }, [loadBoards]);

  const openBoard = (b) => {
    setCurrentId(b.id); setName(b.name);
    setTokens((b.data?.tokens || []).map((t, i) => ({ ...t, id: t.id || `t_${i}_${Date.now()}` })));
    setArrows((b.data?.arrows || []).map((a, i) => ({ ...a, id: a.id || `a_${i}` })));
    setSelected(null); setAiResult(null);
  };
  const newBoard = () => { setCurrentId(null); setName("Untitled board"); setTokens([]); setArrows([]); setSelected(null); setAiResult(null); };

  const pos = (e) => {
    const r = boardRef.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    return { x: Math.max(3, Math.min(97, x)), y: Math.max(3, Math.min(97, y)) };
  };

  const onBoardPointerDown = (e) => {
    if (mode !== "arrow") { setSelected(null); return; }
    const p = pos(e);
    draft.current = { x1: p.x, y1: p.y, x2: p.x, y2: p.y };
    setDraftArrow({ ...draft.current });
    boardRef.current.setPointerCapture(e.pointerId);
  };
  const onBoardPointerMove = (e) => {
    if (dragId.current) {
      const p = pos(e);
      setTokens((ts) => ts.map((t) => (t.id === dragId.current ? { ...t, x: p.x, y: p.y } : t)));
    } else if (draft.current) {
      const p = pos(e);
      draft.current = { ...draft.current, x2: p.x, y2: p.y };
      setDraftArrow({ ...draft.current });
    }
  };
  const onBoardPointerUp = () => {
    if (draft.current) {
      const d = draft.current;
      if (Math.hypot(d.x2 - d.x1, d.y2 - d.y1) > 4) setArrows((a) => [...a, { ...d, id: `a_${Date.now()}` }]);
      draft.current = null; setDraftArrow(null);
    }
    dragId.current = null;
  };

  const onTokenPointerDown = (e, t) => {
    if (mode === "arrow") return;
    e.stopPropagation();
    dragId.current = t.id;
    setSelected(t.id);
    boardRef.current.setPointerCapture(e.pointerId);
  };

  const addToken = (team) => {
    const nums = tokens.filter((t) => t.team === team).length + 1;
    const t = { id: `t_${Date.now()}`, label: String(nums), x: 50, y: team === "home" ? 30 : 70, team };
    setTokens((ts) => [...ts, t]); setSelected(t.id);
  };
  const removeSelected = () => { if (selected) { setTokens((ts) => ts.filter((t) => t.id !== selected)); setSelected(null); } };
  const renameSelected = (v) => setTokens((ts) => ts.map((t) => (t.id === selected ? { ...t, label: v.slice(0, 6) } : t)));

  const loadRoster = async () => {
    try {
      const r = await api.get("/players");
      const list = (r.data || []).slice(0, 11);
      if (!list.length) return toast.error("No athletes to load");
      const cols = Math.min(4, list.length);
      setTokens(list.map((p, i) => ({
        id: `t_${p.id}`, label: p.jersey_number ? String(p.jersey_number) : (p.name || "").slice(0, 3),
        photo: p.photo || "", name: p.name || "",
        x: 15 + (i % cols) * (70 / Math.max(1, cols - 1 || 1)), y: 20 + Math.floor(i / cols) * 18, team: "home",
      })));
      toast.success(`Loaded ${list.length} athletes`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const [exporting, setExporting] = useState(false);
  const snapshot = async () => {
    const node = boardRef.current;
    return toPng(node, { pixelRatio: 2, cacheBust: true, width: node.offsetWidth, height: node.offsetHeight });
  };
  const safeName = () => (name || "tactic-board").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  const exportPng = async () => {
    setExporting(true);
    try {
      const url = await snapshot();
      const a = document.createElement("a"); a.href = url; a.download = `${safeName()}.png`; a.click();
      toast.success("PNG downloaded");
    } catch (e) { toast.error("Export failed"); } finally { setExporting(false); }
  };
  const exportPdf = async () => {
    setExporting(true);
    try {
      const url = await snapshot();
      const node = boardRef.current;
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth();
      const margin = 32;
      const iw = pw - margin * 2;
      const ih = iw * (node.offsetHeight / node.offsetWidth);
      pdf.setFontSize(16); pdf.text(name || "Tactic board", margin, margin + 4);
      pdf.addImage(url, "PNG", margin, margin + 20, iw, ih);
      if (aiResult?.coaching_points?.length) {
        let y = margin + 32 + ih;
        pdf.setFontSize(12); pdf.text("Coaching points", margin, y); y += 16;
        pdf.setFontSize(10);
        aiResult.coaching_points.forEach((c) => { pdf.text(`• ${c}`, margin, y, { maxWidth: iw }); y += 16; });
      }
      pdf.save(`${safeName()}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) { toast.error("Export failed"); } finally { setExporting(false); }
  };

  const save = async () => {
    setSaving(true);
    const data = { tokens, arrows, texts: [] };
    try {
      if (currentId) {
        await api.put(`/tactics/${currentId}`, { name, data });
        toast.success("Board saved");
      } else {
        const r = await api.post("/tactics", { name, sport, data });
        setCurrentId(r.data.id); toast.success("Board created");
      }
      loadBoards();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const delBoard = async () => {
    if (!currentId) return newBoard();
    try { await api.delete(`/tactics/${currentId}`); toast.success("Board deleted"); newBoard(); loadBoards(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const runAI = async () => {
    if (!aiPrompt.trim()) return toast.error("Describe the tactic you want");
    setAiLoading(true); setAiResult(null);
    try {
      const r = await api.post("/tactics/ai", { sport, prompt: aiPrompt });
      const t = (r.data.tokens || []).map((x, i) => ({ ...x, id: `t_ai_${i}_${Date.now()}` }));
      if (t.length) setTokens(t);
      setAiResult({ formation_name: r.data.formation_name, explanation: r.data.explanation, coaching_points: r.data.coaching_points || [] });
      if (r.data.formation_name && !currentId) setName(r.data.formation_name);
      toast.success("AI tactic ready");
    } catch (e) { toast.error(apiErr(e)); } finally { setAiLoading(false); }
  };

  if (loading) return <Loader />;
  if (!isPremium) return (
    <div className="animate-fade-up">
      <PageHeader title="Tactics" subtitle="Design plays and formations for your team." testid="tactics-header" />
      <UpgradeGate feature="Tactic board" desc="Design formations, draw plays and get AI tactic suggestions for your sport. Available on the Pro and Club plans." testid="tactics-upgrade" />
    </div>
  );

  const sel = tokens.find((t) => t.id === selected);

  return (
    <div className="animate-fade-up" data-testid="tactics-page">
      <PageHeader
        title="Tactics"
        subtitle={`AI + custom tactic board for ${sportMeta?.player_term ? sport : sport}.`}
        testid="tactics-header"
        action={
          <>
            <Button variant="outline" onClick={newBoard} data-testid="tactics-new-btn"><Plus size={16} className="mr-1.5" /> New</Button>
            <Button variant="outline" onClick={exportPng} disabled={exporting} data-testid="tactics-export-png"><Download size={16} className="mr-1.5" /> PNG</Button>
            <Button variant="outline" onClick={exportPdf} disabled={exporting} data-testid="tactics-export-pdf"><Download size={16} className="mr-1.5" /> PDF</Button>
            <Button onClick={save} disabled={saving} className="brand-gradient text-white border-0" data-testid="tactics-save-btn">
              {saving ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Save size={16} className="mr-1.5" />} Save
            </Button>
          </>
        }
      />

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-3">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-10 w-full sm:w-56" placeholder="Board name" data-testid="tactics-name-input" />
            {boards.length > 0 && (
              <Select value={currentId || ""} onValueChange={(v) => { const b = boards.find((x) => x.id === v); if (b) openBoard(b); }}>
                <SelectTrigger className="h-10 w-full sm:w-48" data-testid="tactics-board-select"><SelectValue placeholder="Open saved board" /></SelectTrigger>
                <SelectContent>{boards.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant={mode === "move" ? "default" : "outline"} onClick={() => setMode("move")} data-testid="tactics-move-mode"><MousePointer2 size={15} className="mr-1.5" /> Move</Button>
            <Button size="sm" variant={mode === "arrow" ? "default" : "outline"} onClick={() => setMode("arrow")} data-testid="tactics-arrow-mode"><MoveRight size={15} className="mr-1.5" /> Arrow</Button>
            <Button size="sm" variant="outline" onClick={() => addToken("home")} data-testid="tactics-add-home"><span className="h-3 w-3 rounded-full mr-1.5" style={{ background: HOME }} /> Player</Button>
            <Button size="sm" variant="outline" onClick={() => addToken("away")} data-testid="tactics-add-away"><span className="h-3 w-3 rounded-full mr-1.5" style={{ background: AWAY }} /> Opponent</Button>
            <Button size="sm" variant="outline" onClick={loadRoster} data-testid="tactics-load-roster"><Users size={15} className="mr-1.5" /> Roster</Button>
            <Button size="sm" variant="outline" onClick={() => setArrows([])} data-testid="tactics-clear-arrows"><Eraser size={15} className="mr-1.5" /> Arrows</Button>
            <Button size="sm" variant="outline" className="text-destructive" onClick={delBoard} data-testid="tactics-delete-btn"><Trash2 size={15} className="mr-1.5" /> Delete</Button>
          </div>

          {sel && (
            <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-2" data-testid="tactics-selected-bar">
              <span className="text-sm text-muted-foreground">Label:</span>
              <Input value={sel.label} onChange={(e) => renameSelected(e.target.value)} className="h-9 w-24" data-testid="tactics-rename-input" />
              <Button size="sm" variant="outline" className="text-destructive ml-auto" onClick={removeSelected} data-testid="tactics-remove-selected"><X size={14} className="mr-1" /> Remove</Button>
            </div>
          )}

          {/* Board */}
          <div
            ref={boardRef}
            onPointerDown={onBoardPointerDown}
            onPointerMove={onBoardPointerMove}
            onPointerUp={onBoardPointerUp}
            className="relative w-full mx-auto rounded-xl overflow-hidden border border-border select-none touch-none"
            style={{ maxWidth: 560, aspectRatio: "2 / 3" }}
            data-testid="tactics-board"
          >
            <Pitch sport={sport} />
            {/* arrows overlay */}
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none">
              <defs>
                <marker id="ah" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#facc15" /></marker>
              </defs>
              {arrows.map((a) => <line key={a.id} x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} stroke="#facc15" strokeWidth="0.9" markerEnd="url(#ah)" />)}
              {draftArrow && <line x1={draftArrow.x1} y1={draftArrow.y1} x2={draftArrow.x2} y2={draftArrow.y2} stroke="#facc15" strokeWidth="0.9" strokeDasharray="2 1.5" markerEnd="url(#ah)" />}
            </svg>
            {/* tokens */}
            {tokens.map((t) => (
              <div
                key={t.id}
                onPointerDown={(e) => onTokenPointerDown(e, t)}
                className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center text-white text-[11px] font-bold shadow-md border-2 overflow-hidden ${selected === t.id ? "border-yellow-300 ring-2 ring-yellow-300/60" : "border-white/70"}`}
                style={{ left: `${t.x}%`, top: `${t.y}%`, width: 30, height: 30, background: t.team === "away" ? AWAY : HOME, cursor: mode === "move" ? "grab" : "crosshair", pointerEvents: mode === "arrow" ? "none" : "auto" }}
                data-testid={`tactic-token-${t.id}`}
              >
                {t.photo ? <img src={t.photo} alt="" className="w-full h-full object-cover pointer-events-none" /> : t.label}
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground text-center">
            {mode === "arrow" ? "Drag on the pitch to draw a run/pass arrow." : "Drag players to position them. Tap a player to rename or remove."}
          </p>
        </div>

        {/* AI panel */}
        <div className="space-y-3">
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="h-8 w-8 rounded-lg brand-gradient flex items-center justify-center text-white"><Sparkles size={16} /></span>
              <h3 className="font-heading font-bold">AI tactician</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Describe a formation or scenario — AI will lay out the players and explain the plan for your sport.</p>
            <Textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} rows={3} placeholder={`e.g. 4-3-3 high press vs a counter-attacking ${sport} team`} data-testid="tactics-ai-input" />
            <Button onClick={runAI} disabled={aiLoading} className="w-full mt-3 brand-gradient text-white border-0" data-testid="tactics-ai-btn">
              {aiLoading ? <><Loader2 size={16} className="mr-1.5 animate-spin" /> Thinking…</> : <><Sparkles size={16} className="mr-1.5" /> Generate tactic</>}
            </Button>
          </div>
          {aiResult && (
            <div className="rounded-2xl border border-border bg-card p-4 space-y-2" data-testid="tactics-ai-result">
              <p className="font-heading font-bold text-primary">{aiResult.formation_name}</p>
              {aiResult.explanation && <p className="text-sm text-muted-foreground">{aiResult.explanation}</p>}
              {aiResult.coaching_points?.length > 0 && (
                <ul className="text-sm space-y-1.5 mt-2">
                  {aiResult.coaching_points.map((c, i) => (
                    <li key={i} className="flex gap-2"><span className="text-primary">•</span><span>{c}</span></li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
